<template>
  <div>
    <Head :title="__('Report Assigned User')" />

    <!-- Summary Card -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 mb-4 p-5 flex items-center justify-between">
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <icon name="users" class="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <div class="text-2xl font-bold text-indigo-600">{{ totalTickets }}</div>
          <div class="text-sm text-gray-500">{{ __('Total Assigned Tickets') }}</div>
          <div class="text-xs text-gray-400">{{ rows.length }} {{ __('assignees') }}</div>
        </div>
      </div>
      <div class="text-right">
        <div class="text-xs text-gray-400 mb-1">{{ __('Period') }}</div>
        <div class="text-sm font-medium text-gray-600">
          {{ form.all_time ? __('All Time') : (form.date_from + ' — ' + form.date_to) }}
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 mb-4">
      <div class="px-5 py-4 flex flex-wrap gap-3 items-end">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">{{ __('From') }}</label>
          <input
            type="date"
            v-model="form.date_from"
            :disabled="form.all_time"
            class="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-40 disabled:cursor-not-allowed"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">{{ __('To') }}</label>
          <input
            type="date"
            v-model="form.date_to"
            :disabled="form.all_time"
            class="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-40 disabled:cursor-not-allowed"
          />
        </div>
        <button
          type="button"
          @click="toggleAllTime"
          :class="['all-time-toggle', form.all_time ? 'all-time-toggle--on' : 'all-time-toggle--off']"
        >
          <span class="all-time-track"><span class="all-time-knob"></span></span>
          <span class="all-time-label">All Time</span>
        </button>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-500">{{ __('Department') }}</label>
          <select
            v-model="form.department_id"
            class="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          >
            <option :value="null">{{ __('All Departments') }}</option>
            <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
          </select>
        </div>
        <button @click="applyFilter" class="btn-indigo px-4 py-2 text-sm">
          {{ __('Apply') }}
        </button>
        <button @click="resetFilter" class="btn-secondary px-4 py-2 text-sm">
          {{ __('Reset') }}
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
      <table class="w-full whitespace-nowrap ticket_list">
        <tr class="text-left font-bold">
          <th class="w-10">#</th>
          <th>{{ __('Assignee') }}</th>
          <th>{{ __('Department') }}</th>
          <th class="text-center">{{ __('Total Assigned Tickets') }}</th>
        </tr>
        <tr v-for="(row, index) in rows" :key="row.user_id"
            class="hover:bg-gray-100 focus-within:bg-gray-100 ticket-row">
          <td class="border-t">
            <div class="px-4 py-2 text-sm font-medium text-gray-400">{{ index + 1 }}</div>
          </td>
          <td class="border-t">
            <div class="flex items-center gap-2 px-4 py-2">
              <div class="um-avatar w-8 h-8 flex-shrink-0">
                <img v-if="row.photo" :src="row.photo" class="w-full h-full object-cover" />
                <span v-else class="um-avatar-initials text-xs">{{ (row.name || '?')[0].toUpperCase() }}</span>
              </div>
              <div>
                <div class="text-sm font-medium">{{ row.name }}</div>
                <div class="text-xs opacity-60">{{ row.email }}</div>
              </div>
            </div>
          </td>
          <td class="border-t">
            <div class="px-4 py-2 text-sm opacity-70">{{ row.department }}</div>
          </td>
          <td class="border-t text-center">
            <div class="px-4 py-2">
              <span class="inline-block px-3 py-1 rounded-full text-sm font-bold bg-indigo-100 text-indigo-700">
                {{ row.total }}
              </span>
            </div>
          </td>
        </tr>
        <tr v-if="rows.length === 0">
          <td class="px-4 py-6 border-t text-sm text-gray-400 text-center" colspan="4">
            {{ __('No assigned tickets found for the selected period.') }}
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'

export default {
  components: { Head, Icon },
  layout: Layout,
  props: {
    filters: Object,
    rows: Array,
    totalTickets: Number,
    departments: Array,
  },
  data() {
    return {
      form: {
        date_from:     this.filters.date_from,
        date_to:       this.filters.date_to,
        department_id: this.filters.department_id ?? null,
        all_time:      this.filters.all_time || false,
      },
    }
  },
  methods: {
    applyFilter() {
      this.$inertia.get(this.route('report_assigned_user'), {
        ...this.form,
        date_from: this.form.all_time ? '' : this.form.date_from,
        date_to:   this.form.all_time ? '' : this.form.date_to,
      }, { preserveState: true })
    },
    toggleAllTime() {
      this.form.all_time = !this.form.all_time
      if (!this.form.all_time) {
        const today = new Date().toISOString().slice(0, 10)
        this.form.date_from = today
        this.form.date_to   = today
      }
      this.applyFilter()
    },
    resetFilter() {
      const today = new Date().toISOString().slice(0, 10)
      this.form = { date_from: today, date_to: today, department_id: null, all_time: false }
      this.$inertia.get(this.route('report_assigned_user'), this.form, { preserveState: true })
    },
  },
}
</script>

<style scoped>
.all-time-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 12px 5px 6px;
    border-radius: 999px;
    border: 1.5px solid;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, color 0.2s;
    outline: none;
    user-select: none;
}
.all-time-toggle--off {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #6b7280;
}
.all-time-toggle--off:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #374151;
}
.all-time-toggle--on {
    background: #f0fdf4;
    border-color: #15803d;
    color: #15803d;
}
.all-time-toggle--on:hover { background: #dcfce7; }
.all-time-track {
    position: relative;
    width: 32px;
    height: 18px;
    border-radius: 999px;
    transition: background 0.2s;
    flex-shrink: 0;
}
.all-time-toggle--off .all-time-track { background: #d1d5db; }
.all-time-toggle--on  .all-time-track { background: #15803d; }
.all-time-knob {
    position: absolute;
    top: 2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    transition: left 0.2s cubic-bezier(.4,0,.2,1);
}
.all-time-toggle--off .all-time-knob { left: 2px; }
.all-time-toggle--on  .all-time-knob { left: 16px; }
.all-time-label { line-height: 1; }

:global(.dark) .all-time-toggle--off {
    background: #1f2937;
    border-color: #4b5563;
    color: #9ca3af;
}
:global(.dark) .all-time-toggle--off:hover {
    background: #374151;
    border-color: #6b7280;
    color: #d1d5db;
}
:global(.dark) .all-time-toggle--on {
    background: rgba(21,128,61,0.15);
    border-color: #4ade80;
    color: #4ade80;
}
:global(.dark) .all-time-toggle--on .all-time-track { background: #15803d; }
</style>
